import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { notify } from '../../components/common/AppToast';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import kkpDistribService from '../../services/kkpDistribService';
import { KKP_DISTRIB_MOIS } from '../../utils/kkpDistribPalette';
import {
  AppBadge,
  AppButton,
  AppCard,
  AppFormField,
  AppSelect,
  AppTextarea,
} from '../../components/ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-distrib-page.scss';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const emptyForm = (mois) => ({
  texte: '',
  point_id: '',
  mois,
});

const KkpDistribCommentairesPage = () => {
  const { ask, modal } = useAppConfirm();
  const [annee, setAnnee] = useState(currentYear);
  const [data, setData] = useState({ mois: [] });
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMois, setOpenMois] = useState(currentMonth);
  const [form, setForm] = useState(emptyForm(currentMonth));
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [yearData, list] = await Promise.all([
        kkpDistribService.listCommentaires({ annee }),
        kkpDistribService.listPoints({ limit: 1000 }),
      ]);
      setData(yearData);
      setPoints(list.points ?? []);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [annee]);

  useEffect(() => {
    load();
  }, [load]);

  const pointOptions = useMemo(
    () => [...points].sort((a, b) => String(a.nom).localeCompare(String(b.nom), 'fr')),
    [points],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.texte.trim()) return;
    setSaving(true);
    try {
      const payload = {
        annee,
        mois: form.mois,
        texte: form.texte.trim(),
        point_id: form.point_id ? Number(form.point_id) : null,
      };
      if (editingId) {
        await kkpDistribService.updateCommentaire(editingId, payload);
        notify.success('Commentaire mis à jour');
      } else {
        await kkpDistribService.createCommentaire(payload);
        notify.success('Commentaire ajouté');
      }
      setForm(emptyForm(form.mois));
      setEditingId(null);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (commentaire) => {
    setOpenMois(commentaire.mois);
    setEditingId(commentaire.id);
    setForm({
      texte: commentaire.texte,
      point_id: commentaire.point_id ? String(commentaire.point_id) : '',
      mois: commentaire.mois,
    });
  };

  const handleDelete = async (commentaire) => {
    if (!(await ask({
      title: 'Supprimer le commentaire',
      message: 'Supprimer ce commentaire ?',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    }))) return;
    try {
      await kkpDistribService.deleteCommentaire(commentaire.id);
      if (editingId === commentaire.id) {
        setEditingId(null);
        setForm(emptyForm(openMois));
      }
      notify.success('Commentaire supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Commentaires de tournée"
        subtitle="Notes mensuelles associées à un point de dépôt"
        actions={(
          <AppSelect
            size="sm"
            className="w-auto"
            value={annee}
            onChange={(e) => setAnnee(Number(e.target.value))}
            aria-label="Année"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </AppSelect>
        )}
      />

      {loading ? (
        <KkpPlanningEmptyState loading />
      ) : (
        <div className="row g-3">
          <div className="col-lg-4">
            <div className="card">
              <div className="list-group list-group-flush">
                {(data.mois ?? []).map((block) => (
                  <button
                    key={block.mois}
                    type="button"
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${openMois === block.mois ? 'active' : ''}`}
                    onClick={() => {
                      setOpenMois(block.mois);
                      if (!editingId) setForm((f) => ({ ...f, mois: block.mois }));
                    }}
                  >
                    <span>{block.label}</span>
                    <AppBadge variant={openMois === block.mois ? 'neutral' : 'secondary'} className={openMois === block.mois ? 'bg-light text-dark' : ''}>
                      {block.count}
                    </AppBadge>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            <AppCard
              title={editingId ? 'Modifier le commentaire' : `Nouveau commentaire · ${KKP_DISTRIB_MOIS.find((m) => m.value === form.mois)?.label || ''}`}
              className="mb-3"
            >
                <form onSubmit={handleSubmit}>
                  <AppFormField label="Point de dépôt" htmlFor="comm-point">
                    <AppSelect
                      id="comm-point"
                      value={form.point_id}
                      onChange={(e) => setForm((f) => ({ ...f, point_id: e.target.value }))}
                    >
                      <option value="">Sans point (note générale)</option>
                      {pointOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nom}{p.ville ? ` · ${p.ville}` : ''}
                        </option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField label="Commentaire" htmlFor="comm-texte" required>
                    <AppTextarea
                      id="comm-texte"
                      rows={4}
                      required
                      placeholder="Ex. fermé le lundi, nouveau présentoir, quantité à confirmer…"
                      value={form.texte}
                      onChange={(e) => setForm((f) => ({ ...f, texte: e.target.value }))}
                    />
                  </AppFormField>
                  <div className="d-flex gap-2">
                    <AppButton type="submit" loading={saving} loadingLabel="Enregistrement…">
                      {editingId ? 'Mettre à jour' : 'Ajouter'}
                    </AppButton>
                    {editingId ? (
                      <AppButton
                        variant="outline-secondary"
                        onClick={() => {
                          setEditingId(null);
                          setForm(emptyForm(openMois));
                        }}
                      >
                        Annuler
                      </AppButton>
                    ) : null}
                  </div>
                </form>
            </AppCard>

            {((data.mois ?? []).find((m) => m.mois === openMois)?.commentaires ?? []).length === 0 ? (
              <KkpPlanningEmptyState message="Aucun commentaire pour ce mois." />
            ) : (
              <div className="d-flex flex-column gap-2">
                {(data.mois.find((m) => m.mois === openMois)?.commentaires ?? []).map((commentaire) => (
                  <AppCard key={commentaire.id}>
                      <div className="d-flex justify-content-between gap-3">
                        <div>
                          {commentaire.point ? (
                            <div className="fw-semibold mb-1">
                              {commentaire.point.nom}
                              {commentaire.point.ville ? (
                                <span className="text-secondary-light fw-normal"> · {commentaire.point.ville}</span>
                              ) : null}
                            </div>
                          ) : (
                            <div className="text-secondary-light text-sm mb-1">Note générale</div>
                          )}
                          <div className="kkp-distrib-comment mb-0" style={{ maxWidth: 'none' }}>
                            {commentaire.texte}
                          </div>
                        </div>
                        <div className="text-nowrap">
                          <AppButton
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            title="Modifier"
                            icon="mdi:pencil-outline"
                            onClick={() => startEdit(commentaire)}
                          />
                          <AppButton
                            size="sm"
                            variant="outline-danger"
                            title="Supprimer"
                            icon="mdi:delete-outline"
                            onClick={() => handleDelete(commentaire)}
                          />
                        </div>
                      </div>
                  </AppCard>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {modal}
    </MasterLayout>
  );
};

export default KkpDistribCommentairesPage;
